<script setup lang="ts" generic="TSubject extends TSchoolSubjectListItem">
import type { TCompetenceDomainListItem, TSchoolSubjectListItem } from '@/interfaces';
import CustomCheckbox from '@/core/elements/CustomCheckbox.vue';
import CustomRadioButton from '@/core/elements/CustomRadioButton.vue';
import SchoolSubjectBadge from '@/core/elements/SchoolSubjectBadge.vue';
import SchoolSubjectCompetenceInfoTooltip from '@/core/elements/SchoolSubjectCompetenceInfoTooltip.vue';
import { immutableSort } from 'common/utils/array.utils';
import { computed, shallowRef, toRefs, watch } from 'vue';

const emit = defineEmits<{
  'update:modelValue': [value: TSubject[]];
}>();

const props = defineProps<{
  modelValue: TSubject[];
  availableCompetenceDomains: TCompetenceDomainListItem[];
  availableSchoolSubjects: TSubject[];
  disabled?: boolean;
  readOnly?: boolean;
  singleSelect?: boolean;
}>();
const { modelValue, availableSchoolSubjects } = toRefs(props);

const selectedSchoolSubjectsInternal = shallowRef<TSubject[]>([]);
const orderedSchoolSubjects = computed(() => immutableSort(availableSchoolSubjects.value, (a, b) => a.order - b.order));

const isSelected = (id: number): boolean => {
  return selectedSchoolSubjectsInternal.value.some((subject) => subject.id === id);
};

const toggleSchoolSubjectSelected = (subject: TSubject, value: boolean): void => {
  if (props.readOnly || props.disabled) return;
  if (props.singleSelect) {
    selectedSchoolSubjectsInternal.value = [subject];
  } else if (value) {
    selectedSchoolSubjectsInternal.value = [...selectedSchoolSubjectsInternal.value, subject];
  } else {
    selectedSchoolSubjectsInternal.value = selectedSchoolSubjectsInternal.value.filter((s) => s.id !== subject.id);
  }
  emit('update:modelValue', selectedSchoolSubjectsInternal.value);
};

watch(
  modelValue,
  () => {
    selectedSchoolSubjectsInternal.value = modelValue.value;
  },
  { immediate: true },
);
</script>

<template>
  <div class="flex gap-md flex-col">
    <div v-for="subject in orderedSchoolSubjects" :key="subject.id" class="flex gap-sm items-center min-h-[27px]">
      <div
        class="flex items-center gap-md"
        :class="!readOnly && !disabled && 'cursor-pointer select-none'"
        @click.prevent="toggleSchoolSubjectSelected(subject, !isSelected(subject.id))"
      >
        <!-- the radio brings role, aria-checked and keyboard handling of its own, so it stays clickable -->
        <CustomRadioButton
          v-if="singleSelect && !disabled && !readOnly"
          name="school-subject-single-select"
          :model-value="isSelected(subject.id)"
          @update:model-value="toggleSchoolSubjectSelected(subject, true)"
        />
        <!-- the row owns the mouse (pointer-events-none), the checkbox owns focus and keyboard -->
        <CustomCheckbox
          v-else-if="!disabled && !readOnly"
          :model-value="isSelected(subject.id)"
          :label="subject.name"
          :value="subject.id"
          class="pointer-events-none"
          @update:model-value="toggleSchoolSubjectSelected(subject, $event)"
        />
        <SchoolSubjectBadge :school-subject="subject" />
        <span class="text-base leading-[170%]">
          {{ subject.name }}
          <span class="inline-block align-top mt-[2px]">
            <SchoolSubjectCompetenceInfoTooltip
              v-if="!disabled"
              :school-subject="subject"
              :available-competence-domains="availableCompetenceDomains"
            />
          </span>
        </span>
      </div>
    </div>
  </div>
</template>
